import {
  CaretDownOutlined,
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Badge, Space, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import {
  LOGIN_PATH,
  REDIRECT_QUERY_KEY,
  SELECT_CONTEXT_PATH,
} from '@/constants/auth';
import { useAuthStore } from '@/stores/auth';
import { useRouteStore } from '@/stores/route';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const shopName = currentUser?.shopName || '订单中台';

  return (
    <Space
      size={4}
      style={{ display: 'flex', alignItems: 'center', lineHeight: 'normal' }}
    >
      <Badge status="success" style={{ transform: 'scale(0.8)' }} />
      <span style={{ fontSize: 14, fontWeight: 500, color: '#061b31' }}>
        {shopName}
      </span>
      <CaretDownOutlined
        style={{ fontSize: 12, color: '#8aa0b8', marginLeft: 2 }}
      />
    </Space>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '36px', // 统一为 36px，不再撑满 48px
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 12px',
      cursor: 'pointer',
      borderRadius: '8px', // 更明显的圆角
      transition: 'all 0.3s',
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    dropdown: {
      '.ant-dropdown-menu-item': {
        transition: 'all 0.3s',
        '&:not(.ant-dropdown-menu-item-disabled):hover': {
          color: `${token.colorPrimary} !important`,
          backgroundColor: `${token.colorPrimaryBg} !important`,
        },
        '&:not(.ant-dropdown-menu-item-disabled):hover .ant-dropdown-menu-item-icon':
          {
            color: `${token.colorPrimary} !important`,
          },
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await useAuthStore.getState().logout();
    useRouteStore.getState().reset();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      [REDIRECT_QUERY_KEY]: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // 只在当前没有 redirect 参数时补默认回跳地址。
    if (window.location.pathname !== LOGIN_PATH && !redirect) {
      history.replace({
        pathname: LOGIN_PATH,
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...(s || {}), currentUser: undefined }));
      });
      void loginOut();
      return;
    }
    if (key === SELECT_CONTEXT_PATH) {
      history.push(SELECT_CONTEXT_PATH);
      return;
    }

    history.push(String(key));
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser?.name) {
    return loading;
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      disabled: true,
      label: (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '4px 0',
          }}
        >
          <span style={{ fontSize: 12, color: '#8aa0b8', lineHeight: 1 }}>
            当前登录
          </span>
          <span style={{ fontSize: 14, color: '#061b31', fontWeight: 500 }}>
            {currentUser.name}
          </span>
        </div>
      ),
    },
    {
      type: 'divider' as const,
    },
    {
      key: '/home/user',
      icon: <UserOutlined />,
      label: '个人中心',
    },
    {
      type: 'divider' as const,
    },
    {
      key: SELECT_CONTEXT_PATH,
      icon: <SwapOutlined />,
      label: '切换店铺',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      overlayClassName={styles.dropdown}
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
